import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import classNames from 'classnames';
import { Fragment, ReactNode } from 'react';
import { MdClose } from 'react-icons/md';

import { BottomSheet } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useModalSize from '@/hooks/useModalSize';
import { PropsWithClassName, Size } from '@/types';
import { cn } from '@/utils';

export interface DialogProps {
  content: ReactNode;
  title?: string;
  size?: Size;
  onClose: () => void;
  isOpen: boolean;
}

const GdtDialog = ({
  content,
  title,
  className,
  size = 'md',
  isOpen,
  onClose,
}: PropsWithClassName<DialogProps>) => {
  const { isDesktop } = useIsDesktop();
  const dialogSizeClass = useModalSize(size);

  const contentClass = classNames(
    'max-w-lg relative px-14 pb-8 inline-block w-full overflow-hidden text-left align-middle transition-all transform bg-white border border-black border-opacity-5 shadow-xl rounded-[11px]',
    dialogSizeClass
  );

  if (isDesktop) {
    return (
      <>
        <Dialog
          transition
          as="div"
          className="fixed inset-0 z-10 overflow-y-auto focus:outline-none transition duration-75 ease-in-out data-[closed]:opacity-0"
          open={isOpen}
          onClose={onClose}
        >
          {/* The backdrop, rendered as a fixed sibling to the panel container */}
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-neutral-900 bg-opacity-50 transition duration-75 ease-in-out data-[closed]:opacity-0"
            onClick={onClose}
          />
          {/* Full-screen container to center the panel */}
          <div
            className={cn(
              'fixed inset-0 flex items-center justify-center p-4 min-h-screen px-1 text-center md:px-4',
              `max-width-[${dialogSizeClass}]`
            )}
          >
            {/* The actual dialog panel  */}
            <DialogPanel
              transition
              className={cn(
                'transition duration-75 ease-in-out data-[closed]:scale-95 scale-100',
                contentClass
              )}
            >
              <div>
                <DialogTitle
                  as="h3"
                  className={
                    title
                      ? 'mt-10 mb-5 text-base text-gdt-gray font-bold text-center'
                      : 'h-[15px]'
                  }
                >
                  {title}
                </DialogTitle>
                <button
                  className={`absolute right-4 top-4 rounded-lg`}
                  data-testid="close-modal"
                  type="button"
                  onClick={onClose}
                >
                  <MdClose className="w-[1.6rem] h-[1.6rem] text-gdt-gray-4" />
                </button>
              </div>
              <div className={className}>{content}</div>
            </DialogPanel>
          </div>
        </Dialog>
      </>
    );
  }

  return (
    <Fragment>
      <BottomSheet
        disableDrag={false}
        isOpen={isOpen}
        title={title}
        onClose={onClose}
      >
        {content}
      </BottomSheet>
    </Fragment>
  );
};

export default GdtDialog;
